import { Component, input, OnInit, ViewChild, inject } from '@angular/core';
import {
  AppModelModalComponent,
  AppDialogRef,
  AppDialogService,
  AuthService,
  Column,
  DataTableComponent,
  DateTimePipe,
  EmptyTableComponent,
  ErrorComponent,
  HeaderBarComponent,
  IconAction,
  NimmstaCommonsModuleConfiguration,
  NimmstaTimeAgoPipe,
  ServiceType,
} from '@nimmsta/ng-commons-lib';
import { HttpErrorResponse } from '@angular/common/http';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import { PlatformPipe } from './platform.pipe';
import { ConfigurationService } from '../../services/configuration.service';
import { Configuration } from '../../models/configuration';
import { ConfigurationDatasource } from '../../services/configuration.datasource';
import { OrganizationNameService } from './organization-name.service';
import { ConfigurationOrgFormComponent } from './configuration-org-form.component';
import { OrganizationNamePipe } from './organizationName.pipe';
import { ConfigurationDownloadFormComponent } from './configuration-download-form.component';
import { ConfigStatePipe } from './config-state.pipe';
import { ClearDownloadAction } from './clear-download-action';
import { PendingDownloadAction } from './pending-download-action';

@Component({
  selector: 'app-configuration-list',
  templateUrl: './configuration-list.component.html',
  imports: [
    ConfigurationOrgFormComponent,
    TranslatePipe,
    DataTableComponent,
    EmptyTableComponent,
    AppModelModalComponent,
    HeaderBarComponent,
    ErrorComponent,
  ],
  providers: [
    DateTimePipe,
    NimmstaTimeAgoPipe,
    PlatformPipe,
    OrganizationNamePipe,
    ConfigStatePipe,
  ],
})
export class ConfigurationListComponent implements OnInit {
  private configurationService = inject(ConfigurationService);
  private dateTimePipe = inject(DateTimePipe);
  private timeAgoPipe = inject(NimmstaTimeAgoPipe);
  private platformPipe = inject(PlatformPipe);
  private configuration = inject(NimmstaCommonsModuleConfiguration);
  private organizationService = inject(OrganizationNameService);
  private translate = inject(TranslateService);
  private organizationNamePipe = inject(OrganizationNamePipe);
  private configStatePipe = inject(ConfigStatePipe);
  private modalService = inject(AppDialogService);
  private authService = inject(AuthService);

  @ViewChild(DataTableComponent, { static: true })
  private tableComponent!: DataTableComponent<Configuration>;

  @ViewChild('EditOrgModal', { static: false })
  private editOrgModalComponent!: AppModelModalComponent<Configuration>;

  @ViewChild(ConfigurationOrgFormComponent, { static: false })
  private editOrgFormComponent!: ConfigurationOrgFormComponent;

  isAdmin = input<boolean>(true);

  source!: ConfigurationDatasource;

  error?: HttpErrorResponse;

  downloadModalRef?: AppDialogRef;

  get columns(): Column[] {
    if (this.isAdmin()) {
      return [
        new Column('name', 'configurations.name', { sortable: true }),
        new Column('description', 'configurations.description'),
        new Column('organizationIds', 'organizations.title', {
          asyncPipe: this.organizationNamePipe,
        }),
        new Column('createdAt', 'createdAt', {
          sortable: true,
          pipe: this.dateTimePipe,
        }),
        new Column('lastUpdatedAt', 'lastUpdatedAt', {
          sortable: true,
          pipe: this.timeAgoPipe,
          invertSorting: true,
        }),
        new Column('platform', 'configurations.platform', {
          sortable: true,
          pipe: this.platformPipe,
        }),
        new Column('version', 'configurations.version', { sortable: true }),
        new Column('state', 'configurations.state', {
          sortable: true,
          pipe: this.configStatePipe,
        }),
      ];
    }
    return [
      new Column('name', 'configurations.name', { sortable: true }),
      new Column('description', 'configurations.description'),
      new Column('createdAt', 'createdAt', {
        sortable: true,
        pipe: this.dateTimePipe,
      }),
      new Column('lastUpdatedAt', 'lastUpdatedAt', {
        sortable: true,
        pipe: this.timeAgoPipe,
        invertSorting: true,
      }),
      new Column('platform', 'configurations.platform', {
        sortable: true,
        pipe: this.platformPipe,
      }),
      new Column('version', 'configurations.version', { sortable: true }),
      new Column('state', 'configurations.state', {
        sortable: true,
        pipe: this.configStatePipe,
      }),
    ];
  }

  iconActions: IconAction<Configuration>[] = [];

  serviceType: ServiceType;

  ServiceType = ServiceType;

  constructor() {
    this.serviceType = this.configuration.serviceType;
  }

  ngOnInit() {
    this.source = new ConfigurationDatasource(
      this.configurationService,
      this.organizationService,
    );
    this.source.forAdmin = this.isAdmin();
    const editAction = new IconAction('edit', 'edit');
    editAction.subscribe((id) => this.edit(id));
    this.iconActions.push(editAction);
    const initWithIdAction = new IconAction(
      'configurations.initWithId',
      'new_window',
    );
    initWithIdAction.subscribe((id) => this.initNewWithId(id));
    this.iconActions.push(initWithIdAction);
    if (this.isAdmin()) {
      const orgAction = new IconAction(
        'configurations.editOrgAccess',
        'groups',
      );
      orgAction.subscribe((id) => this.editOrgs(id));
      this.iconActions.push(orgAction);
    }
    const deleteAction = new IconAction('delete', 'delete', true);
    deleteAction.subscribe((id) => this.delete(id));
    this.iconActions.push(deleteAction);
    const clearDownloadAction = new ClearDownloadAction((id) => {
      const configuration = this.source.data?.content.find((c) => c.id === id);
      return !!configuration?.downloadLink;
    });
    clearDownloadAction.subscribe((id) => this.download(id));
    this.iconActions.push(clearDownloadAction);
    const pendingDownloadAction = new PendingDownloadAction((id) => {
      const configuration = this.source.data?.content.find((c) => c.id === id);
      return !!configuration?.downloadLink;
    });
    pendingDownloadAction.subscribe((id) => this.initiatePendingDownload(id));
    this.iconActions.push(pendingDownloadAction);
    const downloadConfigAction = new IconAction(
      'configurations.downloadConfig',
      'file_save',
    );
    downloadConfigAction.subscribe((id) => this.downloadConfig(id));
    this.iconActions.push(downloadConfigAction);
  }

  public createNew() {
    const origin = this.isAdmin() ? 'management' : 'cloud';
    const orgId = this.authService.getActingOrganizationId();
    window.open(
      `${this.configuration.applicationUrls.configurations}` +
        `/v${this.configuration.defaultConfigurationVersion}?origin=${origin}&orgId=${orgId}`,
      '_blank',
    );
  }

  public initNewWithId(id: string) {
    const configuration = this.source.data?.content.find((c) => c.id === id);
    if (!configuration) {
      return;
    }
    const origin = this.isAdmin() ? 'management' : 'cloud';
    const orgId = this.authService.getActingOrganizationId();
    window.open(
      `${this.configuration.applicationUrls.configurations}` +
        `/v${
          configuration.version
        }?origin=${origin}&platform=${configuration.platform.toLowerCase()}&initId=${id}&orgId=${orgId}`,
      '_blank',
    );
  }

  private download(id: string) {
    const configuration = this.source.data?.content.find((c) => c.id === id);
    if (!configuration || !configuration.downloadLink) {
      return;
    }
    const link = configuration.downloadLink;
    window.open(link, '_blank');
  }

  private downloadConfig(id: string) {
    this.configurationService.getConfigurationFile(id).subscribe({
      next: (response) => {
        const filename =
          response.headers
            .get('Content-Disposition')
            ?.split('filename=')[1]
            .replace(/"/g, '') ?? 'configuration.nimmstaconfig';
        const blob = response.body;
        if (!blob) {
          throw new Error('No file data received');
        }
        const blobUrl = window.URL.createObjectURL(blob);

        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);

        URL.revokeObjectURL(blobUrl);
      },
      error: (error) => {
        this.error = error;
      },
    });
  }

  private initiatePendingDownload(id: string) {
    const configuration = this.source.data?.content.find((c) => c.id === id);
    if (!configuration) {
      return;
    }
    this.downloadModalRef = this.modalService.open(
      ConfigurationDownloadFormComponent,
      { data: { configuration } },
    );
    this.downloadModalRef.closed.subscribe(() => {
      this.downloadModalRef = undefined;
    });
  }

  editOrgs(id: string) {
    if (!this.isAdmin()) {
      return;
    }
    this.translate.get('configurations.editOrgAccess').subscribe((value) => {
      this.openOrgModal(value, true, this.getSelectedObject(id)!);
    });
  }

  private edit(id: string) {
    if (!this.source.data?.content.length) {
      return;
    }
    const element = this.source.data.content.find((c) => c.id === id);
    if (!element) {
      return;
    }
    const origin = this.isAdmin() ? 'management' : 'cloud';
    const orgId = this.authService.getActingOrganizationId();
    const url =
      `${this.configuration.applicationUrls.configurations}/v${element.version}` +
      `?id=${
        element.id
      }&platform=${element.platform.toLowerCase()}&origin=${origin}&orgId=${orgId}`;
    window.open(url, '_blank');
  }

  private delete(id: string) {
    const configuration = this.source.data?.content.find((c) => c.id === id);
    if (!configuration) {
      return;
    }
    this.configurationService.delete(configuration).subscribe({
      next: () => this.tableComponent.loadDataPage(),
      error: (error: HttpErrorResponse) => {
        this.error = error;
      },
    });
  }

  onSave(): void {
    this.editOrgFormComponent.onSave();
  }

  onModalClose(): void {
    if (!this.isAdmin()) {
      return;
    }
    this.editOrgModalComponent.hide();
    this.tableComponent.loadDataPage();
  }

  onLoading(loading: boolean): void {
    this.editOrgModalComponent.loading = loading;
  }

  private openOrgModal(
    title: string,
    editable: boolean,
    configuration: Configuration,
  ): void {
    this.editOrgFormComponent.editable = editable;
    this.editOrgFormComponent.setValue(configuration);
    this.editOrgModalComponent.title = title;
    this.editOrgModalComponent.editable = editable;
    this.editOrgModalComponent.show();
  }

  private getSelectedObject(id: string): Configuration | undefined {
    return this.source.data?.content.filter((d) => id === d.id).pop();
  }
}
